/**
 * Stale deployment recovery: when hashed chunks 404 after a new release,
 * reload once so the browser picks up the current index.html + assets.
 *
 * React.lazy() / dynamic import failures are handled inside React (they become
 * render errors), so they do NOT surface as window "unhandledrejection" — the
 * global listeners in App.tsx alone are not enough; use isChunkLoadErrorReason
 * in an error-boundary fallback as well.
 */
/** Set before a recovery reload; do not clear on App mount (lazy chunks load after mount). */
export const CHUNK_RELOAD_KEY = 'cz_chunk_reload';

export function isChunkLoadErrorReason(err: unknown): boolean {
  if (err == null) return false;
  const e = err as Error & { name?: string };
  const name = e.name || '';
  const message = (typeof e.message === 'string' && e.message) || String(err);
  return (
    name === 'ChunkLoadError' ||
    /chunk/i.test(message) ||
    /loading css chunk/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /cannot find module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

/** window "error" / "unhandledrejection" (e.g. CSS chunk, non-React imports) */
export function isChunkLoadErrorEvent(event: any): boolean {
  const error = event?.error ?? event?.reason;
  if (isChunkLoadErrorReason(error)) return true;
  const message =
    (error && typeof (error as Error).message === 'string' && (error as Error).message) ||
    event?.message ||
    '';
  return (
    /chunk/i.test(message) ||
    /loading css chunk/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /cannot find module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

export function reloadOnceForChunkError(): void {
  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.reload();
  }
}
